import React, { useEffect, useRef, useState } from 'react';

/**
 * Textual magnifier lens that follows the mouse cursor.
 * Detects the text element under the cursor and displays it enlarged
 * in a floating bubble next to the pointer. Sharp text at any size.
 */
const TEXT_TAGS = new Set([
  'P', 'SPAN', 'A', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'BUTTON', 'LABEL', 'STRONG', 'EM', 'TD', 'TH',
  'BLOCKQUOTE', 'FIGCAPTION', 'SUMMARY', 'DT', 'DD', 'CAPTION',
]);

const MAX_TEXT_LENGTH = 220;

const findTextElement = (el) => {
  let node = el;
  while (node && node !== document.body) {
    if (node.dataset?.a11yWidget) return null; // ignore the widget itself
    if (TEXT_TAGS.has(node.tagName)) {
      const text = (node.innerText || '').trim();
      if (text.length > 0) return { node, text };
    }
    node = node.parentElement;
  }
  return null;
};

export const MagnifierLens = ({ enabled }) => {
  const [pos, setPos] = useState({ x: -9999, y: -9999 });
  const [text, setText] = useState('');
  const rafRef = useRef(null);
  const lastEventRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setPos({ x: -9999, y: -9999 });
      setText('');
      return;
    }

    const handleMove = (e) => {
      lastEventRef.current = e;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const ev = lastEventRef.current;
        if (!ev) return;
        setPos({ x: ev.clientX, y: ev.clientY });
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        if (!el) {
          setText('');
          return;
        }
        const found = findTextElement(el);
        if (found) {
          const t = found.text.length > MAX_TEXT_LENGTH
            ? found.text.slice(0, MAX_TEXT_LENGTH).trim() + '…'
            : found.text;
          setText(t);
        } else {
          setText('');
        }
      });
    };

    const handleLeave = () => {
      setPos({ x: -9999, y: -9999 });
      setText('');
    };

    window.addEventListener('mousemove', handleMove, { passive: true });
    window.addEventListener('mouseleave', handleLeave);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseleave', handleLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  // Position bubble offset from cursor; flip side near right edge
  const offset = 24;
  const bubbleWidth = 360;
  const bubbleMaxHeight = 220;
  const placeRight = pos.x + offset + bubbleWidth < window.innerWidth;
  const left = placeRight ? pos.x + offset : pos.x - offset - bubbleWidth;
  const top = Math.min(
    Math.max(pos.y + offset, 8),
    window.innerHeight - bubbleMaxHeight - 8
  );

  return (
    <div
      data-a11y-widget="lens"
      aria-hidden="true"
      style={{
        position: 'fixed',
        left,
        top,
        width: bubbleWidth,
        maxHeight: bubbleMaxHeight,
        pointerEvents: 'none',
        zIndex: 2147483600,
        background: 'rgba(17, 24, 39, 0.96)',
        color: '#fff',
        fontSize: 28,
        lineHeight: 1.35,
        padding: '16px 18px',
        borderRadius: 16,
        boxShadow:
          '0 20px 50px -12px rgba(0,0,0,0.45), 0 0 0 2px rgba(20, 184, 166, 0.55)',
        overflow: 'hidden',
        opacity: text ? 1 : 0,
        transition: 'opacity 120ms ease-out',
        fontFamily: 'inherit',
        fontWeight: 500,
        letterSpacing: 0.1,
      }}
    >
      {text}
    </div>
  );
};

export default MagnifierLens;
